import { FC, useMemo, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { DollarSign, TrendingUp, Calendar, Eye, EyeOff, ChevronDown, ChevronUp } from "lucide-react"
import { Sale, InventoryItem } from "../../lib/types"
import { format, startOfMonth, endOfMonth, eachDayOfInterval, subMonths, startOfYear, endOfYear, eachMonthOfInterval, subYears, startOfDay, endOfDay } from "date-fns"
import { useIsMobile } from "../../hooks/use-mobile"

type TimePeriod = 'month' | '6months' | 'year' | 'custom'

type EarningsChartProps = {
  sales: Sale[]
  inventory: InventoryItem[]
}

export const EarningsChart: FC<EarningsChartProps> = ({ sales, inventory }) => {
  const isMobile = useIsMobile()

  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('month')
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null)
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null)
  const [showCosts, setShowCosts] = useState(false)
  const [isChartExpanded, setIsChartExpanded] = useState(isMobile ? false : true)

  // Calculate earnings data based on selected period
  const earningsData = useMemo(() => {
    const now = new Date()
    let startDate: Date
    let endDate: Date
    let intervals: Date[]
    let groupBy: 'day' | 'month'

    switch (selectedPeriod) {
      case 'month':
        startDate = startOfMonth(now)
        endDate = endOfMonth(now)
        intervals = eachDayOfInterval({ start: startDate, end: endDate })
        groupBy = 'day'
        break

      case '6months':
        startDate = startOfMonth(subMonths(now, 5))
        endDate = endOfMonth(now)
        intervals = eachMonthOfInterval({ start: startDate, end: endDate })
        groupBy = 'month'
        break

      case 'year':
        startDate = startOfYear(now)
        endDate = endOfYear(now)
        intervals = eachMonthOfInterval({ start: startDate, end: endDate })
        groupBy = 'month'
        break

      case 'custom':
        if (!customStartDate || !customEndDate) {
          // Default to current month if custom dates not set
          startDate = startOfMonth(now)
          endDate = endOfMonth(now)
          intervals = eachDayOfInterval({ start: startDate, end: endDate })
          groupBy = 'day'
        } else {
          startDate = startOfDay(customStartDate)
          endDate = endOfDay(customEndDate)
          // Use days if range is less than 60 days, otherwise use months
          const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
          if (daysDiff <= 60) {
            intervals = eachDayOfInterval({ start: startDate, end: endDate })
            groupBy = 'day'
          } else {
            intervals = eachMonthOfInterval({ start: startDate, end: endDate })
            groupBy = 'month'
          }
        }
        break
    }

    // Group sales by period
    const earnings = intervals.map(interval => {
      let periodSales: Sale[]

      if (groupBy === 'day') {
        const dateStr = format(interval, 'yyyy-MM-dd')
        periodSales = sales.filter(sale => {
          const saleDate = sale.createdAt.toDate()
          return format(saleDate, 'yyyy-MM-dd') === dateStr
        })
      } else {
        // Group by month
        const monthStart = startOfMonth(interval)
        const monthEnd = endOfMonth(interval)
        periodSales = sales.filter(sale => {
          const saleDate = sale.createdAt.toDate()
          return saleDate >= monthStart && saleDate <= monthEnd
        })
      }

      const totalRevenue = periodSales.reduce((sum, sale) => sum + sale.totalAmount, 0)

      // Calculate estimated costs
      const totalCost = periodSales.reduce((sum, sale) => {
        const inventoryItem = inventory.find(item => item.id === sale.invSourceId)
        if (inventoryItem && inventoryItem.value) {
          // Cost per unit = inventory item value, total cost = value * quantity sold
          return sum + (Number(inventoryItem.value) * sale.quantitySold)
        }
        return sum
      }, 0)

      return {
        date: interval,
        label: groupBy === 'day' ? format(interval, 'MMM d') : format(interval, 'MMM yyyy'),
        shortLabel: groupBy === 'day' ? format(interval, 'd') : format(interval, 'MMM'),
        amount: totalRevenue,
        cost: totalCost
      }
    })

    return earnings
  }, [sales, inventory, selectedPeriod, customStartDate, customEndDate])

  // Calculate statistics
  const totalEarnings = useMemo(() => {
    return earningsData.reduce((sum, day) => sum + day.amount, 0)
  }, [earningsData])

  const totalCosts = useMemo(() => {
    return earningsData.reduce((sum, day) => sum + day.cost, 0)
  }, [earningsData])

  const totalProfit = useMemo(() => {
    return totalEarnings - totalCosts
  }, [totalEarnings, totalCosts])

  const profitMargin = useMemo(() => {
    if (totalEarnings === 0) return 0
    return (totalProfit / totalEarnings) * 100
  }, [totalProfit, totalEarnings])

  const previousPeriodEarnings = useMemo(() => {
    const now = new Date()
    let prevStartDate: Date
    let prevEndDate: Date

    switch (selectedPeriod) {
      case 'month':
        prevStartDate = startOfMonth(subMonths(now, 1))
        prevEndDate = endOfMonth(subMonths(now, 1))
        break

      case '6months':
        prevStartDate = startOfMonth(subMonths(now, 11))
        prevEndDate = endOfMonth(subMonths(now, 6))
        break

      case 'year':
        prevStartDate = startOfYear(subYears(now, 1))
        prevEndDate = endOfYear(subYears(now, 1))
        break

      case 'custom':
        if (!customStartDate || !customEndDate) {
          prevStartDate = startOfMonth(subMonths(now, 1))
          prevEndDate = endOfMonth(subMonths(now, 1))
        } else {
          const daysDiff = Math.ceil((customEndDate.getTime() - customStartDate.getTime()) / (1000 * 60 * 60 * 24))
          prevEndDate = new Date(customStartDate.getTime() - 1)
          prevStartDate = new Date(prevEndDate.getTime() - (daysDiff * 24 * 60 * 60 * 1000))
        }
        break
    }

    return sales
      .filter(sale => {
        const saleDate = sale.createdAt.toDate()
        return saleDate >= prevStartDate && saleDate <= prevEndDate
      })
      .reduce((sum, sale) => sum + sale.totalAmount, 0)
  }, [sales, selectedPeriod, customStartDate, customEndDate])

  const growthPercentage = useMemo(() => {
    if (previousPeriodEarnings === 0) return totalEarnings > 0 ? 100 : 0
    return ((totalEarnings - previousPeriodEarnings) / previousPeriodEarnings) * 100
  }, [totalEarnings, previousPeriodEarnings])

  const getPeriodLabel = () => {
    const now = new Date()
    switch (selectedPeriod) {
      case 'month':
        return format(now, 'MMMM yyyy')
      case '6months':
        return `${format(subMonths(now, 5), 'MMM yyyy')} - ${format(now, 'MMM yyyy')}`
      case 'year':
        return format(now, 'yyyy')
      case 'custom':
        if (!customStartDate || !customEndDate) return 'Custom Range'
        return `${format(customStartDate, 'MMM d, yyyy')} - ${format(customEndDate, 'MMM d, yyyy')}`
    }
  }

  const getComparisonLabel = () => {
    switch (selectedPeriod) {
      case 'month':
        return 'vs last month'
      case '6months':
        return 'vs previous 6 months'
      case 'year':
        return 'vs last year'
      case 'custom':
        return 'vs previous period'
    }
  }

  // Chart dimensions
  const chartWidth = 800
  const chartHeight = 200
  const padding = { top: 20, right: 20, bottom: 30, left: 60 }
  const innerWidth = chartWidth - padding.left - padding.right
  const innerHeight = chartHeight - padding.top - padding.bottom

  // Calculate scales
  const maxValue = showCosts
    ? Math.max(...earningsData.map(d => Math.max(d.amount, d.cost)), 10)
    : Math.max(...earningsData.map(d => d.amount), 10)
  const xScale = innerWidth / Math.max(earningsData.length, 1)
  const yScale = innerHeight / maxValue

  // Generate path for area chart
  const areaPath = useMemo(() => {
    if (earningsData.length === 0) return ''

    const points = earningsData.map((d, i) => {
      const x = padding.left + (i * xScale) + (xScale / 2)
      const y = padding.top + innerHeight - (d.amount * yScale)
      return `${x},${y}`
    })

    const topPath = points.join(' L ')
    const bottomPath = `L ${padding.left + innerWidth},${padding.top + innerHeight} L ${padding.left},${padding.top + innerHeight}`

    return `M ${padding.left},${padding.top + innerHeight} L ${topPath} ${bottomPath} Z`
  }, [earningsData, xScale, yScale, innerHeight, padding])

  // Generate path for line chart
  const linePath = useMemo(() => {
    if (earningsData.length === 0) return ''

    const points = earningsData.map((d, i) => {
      const x = padding.left + (i * xScale) + (xScale / 2)
      const y = padding.top + innerHeight - (d.amount * yScale)
      return `${x},${y}`
    })

    return `M ${points.join(' L ')}`
  }, [earningsData, xScale, yScale, innerHeight, padding])

  // Generate path for cost line
  const costLinePath = useMemo(() => {
    if (earningsData.length === 0 || !showCosts) return ''

    const points = earningsData.map((d, i) => {
      const x = padding.left + (i * xScale) + (xScale / 2)
      const y = padding.top + innerHeight - (d.cost * yScale)
      return `${x},${y}`
    })

    return `M ${points.join(' L ')}`
  }, [earningsData, xScale, yScale, innerHeight, padding, showCosts])

  // Y-axis labels
  const yAxisLabels = useMemo(() => {
    const steps = 4
    return Array.from({ length: steps + 1 }, (_, i) => {
      const value = (maxValue / steps) * i
      const y = padding.top + innerHeight - (value * yScale)
      return { value, y }
    })
  }, [maxValue, yScale, innerHeight, padding])

  return (
    <Card className="dark:bg-neutral800 dark:border-neutral700">
      <CardHeader>
        <div className="space-y-4">
          {/* Header with Title and Period Selector */}
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <CardTitle className="flex items-center justify-between dark:text-neutral100">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-success500" />
                  Earnings Overview
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsChartExpanded(!isChartExpanded)}
                  className="h-8 w-8 p-0"
                >
                  {isChartExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CardTitle>
              <div className="mt-3 space-y-2">
                <div>
                  <div className="text-3xl font-bold text-neutral900 dark:text-neutral100">
                    ${totalEarnings.toFixed(2)}
                  </div>
                  <div className="text-xs text-neutral500 dark:text-neutral400">Revenue</div>
                </div>
                {showCosts && (
                  <div className="flex gap-4 text-sm">
                    <div>
                      <div className="text-lg font-bold text-warning600 dark:text-warning500">
                        ${totalCosts.toFixed(2)}
                      </div>
                      <div className="text-xs text-neutral500 dark:text-neutral400">Costs</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-sumoTeal">
                        ${totalProfit.toFixed(2)}
                      </div>
                      <div className="text-xs text-neutral500 dark:text-neutral400">
                        Profit ({profitMargin.toFixed(1)}%)
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <div className={`flex items-center gap-1 ${growthPercentage >= 0 ? 'text-success600' : 'text-error600'
                    }`}>
                    <TrendingUp width={14} height={14} />
                    <span className="font-semibold">
                      {growthPercentage >= 0 ? '+' : ''}{growthPercentage.toFixed(1)}%
                    </span>
                  </div>
                  <span className="text-neutral500 dark:text-neutral400">{getComparisonLabel()}</span>
                </div>
              </div>
            </div>

            {/* Period Toggle Buttons and Cost Toggle */}
            {isChartExpanded && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-xs text-neutral500 dark:text-neutral400">
                  <Calendar width={14} height={14} />
                  <span>{getPeriodLabel()}</span>
                </div>
                <div className="flex gap-1 flex-wrap">
                  <Button
                    variant={selectedPeriod === 'month' ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedPeriod('month')}
                    className="text-xs px-3 py-1 h-7"
                  >
                    1M
                  </Button>
                  <Button
                    variant={selectedPeriod === '6months' ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedPeriod('6months')}
                    className="text-xs px-3 py-1 h-7"
                  >
                    6M
                  </Button>
                  <Button
                    variant={selectedPeriod === 'year' ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedPeriod('year')}
                    className="text-xs px-3 py-1 h-7"
                  >
                    1Y
                  </Button>
                  <Button
                    variant={selectedPeriod === 'custom' ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedPeriod('custom')}
                    className="text-xs px-3 py-1 h-7"
                  >
                    Custom
                  </Button>
                </div>
                <Button
                  variant={showCosts ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setShowCosts(!showCosts)}
                  className="text-xs px-3 py-1 h-7 flex items-center gap-1"
                >
                  {showCosts ? <Eye width={14} height={14} /> : <EyeOff width={14} height={14} />}
                  {showCosts ? 'Hide' : 'Show'} Costs
                </Button>
              </div>
            )}
          </div>

          {/* Custom Date Range Picker */}
          {isChartExpanded && selectedPeriod === 'custom' && (
            <div className="flex gap-3 items-end bg-neutral50 dark:bg-neutral900 p-3 rounded-md border border-neutral200 dark:border-neutral700">
              <div className="flex-1">
                <label className="text-xs font-medium text-neutral600 dark:text-neutral400 mb-1 block">
                  Start Date
                </label>
                <input
                  type="date"
                  value={customStartDate ? format(customStartDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => setCustomStartDate(e.target.value ? new Date(e.target.value) : null)}
                  className="w-full px-3 py-1.5 text-sm rounded border border-neutral300 dark:border-neutral600 bg-white dark:bg-neutral800 text-neutral900 dark:text-neutral100"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs font-medium text-neutral600 dark:text-neutral400 mb-1 block">
                  End Date
                </label>
                <input
                  type="date"
                  value={customEndDate ? format(customEndDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => setCustomEndDate(e.target.value ? new Date(e.target.value) : null)}
                  className="w-full px-3 py-1.5 text-sm rounded border border-neutral300 dark:border-neutral600 bg-white dark:bg-neutral800 text-neutral900 dark:text-neutral100"
                />
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      {isChartExpanded && (
        <CardContent>
          {earningsData.length > 0 ? (
            <div className="w-full overflow-x-auto">
              <svg
                width={chartWidth}
                height={chartHeight}
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                className="w-full h-auto"
                preserveAspectRatio="xMidYMid meet"
              >
                {/* Grid lines */}
                {yAxisLabels.map((label, i) => (
                  <g key={i}>
                    <line
                      x1={padding.left}
                      y1={label.y}
                      x2={padding.left + innerWidth}
                      y2={label.y}
                      stroke="currentColor"
                      strokeWidth="1"
                      className="text-neutral200 dark:text-neutral700"
                      strokeDasharray="4 4"
                    />
                    <text
                      x={padding.left - 10}
                      y={label.y + 4}
                      textAnchor="end"
                      fontSize="10"
                      className="fill-neutral500 dark:fill-neutral400"
                    >
                      ${label.value.toFixed(0)}
                    </text>
                  </g>
                ))}

                {/* Area fill */}
                <path
                  d={areaPath}
                  className="fill-success500/20 dark:fill-success500/30"
                />

                {/* Revenue Line */}
                <path
                  d={linePath}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  className="text-success500"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* Cost Line */}
                {showCosts && (
                  <path
                    d={costLinePath}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    className="text-warning600 dark:text-warning500"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="6 4"
                  />
                )}

                {/* Data points */}
                {earningsData.map((d, i) => {
                  const x = padding.left + (i * xScale) + (xScale / 2)
                  const yRevenue = padding.top + innerHeight - (d.amount * yScale)
                  const yCost = padding.top + innerHeight - (d.cost * yScale)

                  return (
                    <g key={i}>
                      {/* Revenue point */}
                      {d.amount > 0 && (
                        <>
                          <circle
                            cx={x}
                            cy={yRevenue}
                            r="4"
                            className="fill-white dark:fill-neutral800 stroke-success500"
                            strokeWidth="2"
                          />
                          <circle
                            cx={x}
                            cy={yRevenue}
                            r="8"
                            className="fill-success500/0 hover:fill-success500/20 cursor-pointer transition-all"
                          >
                            <title>{`${d.label}\nRevenue: $${d.amount.toFixed(2)}${showCosts ? `\nCost: $${d.cost.toFixed(2)}\nProfit: $${(d.amount - d.cost).toFixed(2)}` : ''}`}</title>
                          </circle>
                        </>
                      )}

                      {/* Cost point */}
                      {showCosts && d.cost > 0 && (
                        <>
                          <circle
                            cx={x}
                            cy={yCost}
                            r="4"
                            className="fill-white dark:fill-neutral800 stroke-warning600 dark:stroke-warning500"
                            strokeWidth="2"
                          />
                          <circle
                            cx={x}
                            cy={yCost}
                            r="8"
                            className="fill-warning600/0 hover:fill-warning600/20 cursor-pointer transition-all"
                          >
                            <title>{`${d.label}\nCost: $${d.cost.toFixed(2)}`}</title>
                          </circle>
                        </>
                      )}
                    </g>
                  )
                })}

                {/* X-axis labels (show every 5th day) */}
                {earningsData.map((d, i) => {
                  if (i % 5 !== 0 && i !== earningsData.length - 1) return null

                  const x = padding.left + (i * xScale) + (xScale / 2)
                  const y = padding.top + innerHeight + 20

                  return (
                    <text
                      key={i}
                      x={x}
                      y={y}
                      textAnchor="middle"
                      fontSize="10"
                      className="fill-neutral500 dark:fill-neutral400"
                    >
                      {d.shortLabel}
                    </text>
                  )
                })}
              </svg>

              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-0.5 bg-success500"></div>
                  <span className="text-neutral600 dark:text-neutral400">Revenue</span>
                </div>
                {showCosts && (
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-0.5 bg-warning600 dark:bg-warning500" style={{ backgroundImage: 'repeating-linear-gradient(90deg, currentColor 0, currentColor 6px, transparent 6px, transparent 10px)' }}></div>
                    <span className="text-neutral600 dark:text-neutral400">Costs</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-neutral500 dark:text-neutral400">
              <DollarSign className="mb-2 opacity-30" width={48} height={48} />
              <p className="text-sm">No sales data for this month</p>
              <p className="text-xs mt-1">Start selling to see your earnings chart</p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}
